import React, { useState, useEffect } from 'react';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  Title, 
  Tooltip, 
  Legend,
  Filler 
} from 'chart.js';
import { Scatter } from 'react-chartjs-2';
import { 
  Lock, 
  Unlock, 
  TrendingUp, 
  AlertTriangle, 
  Eye, 
  EyeOff, 
  RotateCcw, 
  Plus,
  CheckCircle,
  Settings,
  Brain,
  Loader2,
  FileText
} from 'lucide-react';
import apiService from '../services/apiService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const SmartAdjustments = ({ 
  comparableData = [], 
  subjectProperty = null, 
  onAdjustmentsChange = () => {} 
}) => {

  // State for the new feature-based approach
  const [featureResults, setFeatureResults] = useState(null);
  const [isLoadingFeatures, setIsLoadingFeatures] = useState(false);
  const [error, setError] = useState(null);

  // State for editable values per feature (5-10% less than average)
  const [editableValues, setEditableValues] = useState({});

  // 8 Property Features Configuration
  const FEATURE_CONFIGS = [
    {
      id: 'lotSize',
      title: 'Lot Size Analysis',
      unit: '$/sq ft',
      xAxisLabel: 'Lot Size (sq ft)',
      yAxisLabel: 'Price per Sq Ft ($)',
      color: '#3b82f6'
    },
    {
      id: 'gla', 
      title: 'GLA Analysis',
      unit: '$/sq ft',
      xAxisLabel: 'Gross Living Area (sq ft)',
      yAxisLabel: 'Price per Sq Ft ($)',
      color: '#10b981'
    },
    {
      id: 'fullBaths',
      title: 'Full Bathrooms Analysis', 
      unit: '$',
      xAxisLabel: 'Number of Full Bathrooms',
      yAxisLabel: 'Bathroom Value ($)',
      color: '#f59e0b'
    },
    {
      id: 'halfBaths',
      title: 'Half Bathrooms Analysis',
      unit: '$', 
      xAxisLabel: 'Number of Half Bathrooms',
      yAxisLabel: 'Bathroom Value ($)',
      color: '#ef4444'
    },
    {
      id: 'garageSpaces',
      title: 'Garage Spaces Analysis',
      unit: '$/space',
      xAxisLabel: 'Number of Garage Spaces', 
      yAxisLabel: 'Value per Space ($)',
      color: '#8b5cf6'
    },
    {
      id: 'poolFeatures',
      title: 'Pool Features Analysis',
      unit: '$',
      xAxisLabel: 'Pool Feature',
      yAxisLabel: 'Pool Value ($)',
      color: '#06b6d4'
    },
    {
      id: 'exteriorFeatures', 
      title: 'Exterior Features Analysis',
      unit: '$',
      xAxisLabel: 'Exterior Feature Score',
      yAxisLabel: 'Feature Value ($)',
      color: '#84cc16'
    },
    {
      id: 'conditionQuality',
      title: 'Condition/Quality Analysis',
      unit: '$/grade', 
      xAxisLabel: 'Condition Grade (1-6)',
      yAxisLabel: 'Value per Grade ($)',
      color: '#f97316'
    }
  ];

  // Load feature-based adjustments on component mount
  useEffect(() => {
    if (comparableData.length > 0 && subjectProperty) {
      loadFeatureBasedAdjustments();
    }
  }, [comparableData, subjectProperty]);

  const loadFeatureBasedAdjustments = async () => {
    setIsLoadingFeatures(true);
    setError(null);
    
    try {
      console.log('🏠 Loading feature-based adjustments...');
      
      // Calculate market statistics
      const marketStatistics = calculateMarketStatistics();
      
      // Call the new feature-based endpoint
      const response = await apiService.post('/smart-adjustments/feature-based', {
        subjectProperty,
        comparableProperties: comparableData,
        marketStatistics,
        location: subjectProperty.location || 'Unknown Location'
      });

      if (response.data.success) {
        console.log('✅ Feature-based adjustments loaded successfully');
        setFeatureResults(response.data.featureResults);
        
        // Notify parent component
        onAdjustmentsChange(response.data.featureResults);
      } else {
        throw new Error(response.data.error || 'Failed to load feature adjustments');
      }
      
    } catch (error) {
      console.error('❌ Error loading feature adjustments:', error);
      setError(error.message);
      
      // Load fallback data
      setFeatureResults(generateFallbackFeatureResults());
      
    } finally {
      setIsLoadingFeatures(false);
    }
  };

  const calculateMarketStatistics = () => {
    if (!comparableData.length) return {};
    
    const prices = comparableData.map(comp => comp.ClosePrice || comp.ListPrice || 0).filter(price => price > 0);
    const sqfts = comparableData.map(comp => comp.LivingArea || 0).filter(sqft => sqft > 0);
    
    const averagePrice = prices.reduce((sum, price) => sum + price, 0) / prices.length;
    const averagePricePerSqft = prices.reduce((sum, price, index) => {
      const sqft = sqfts[index] || 1;
      return sum + (price / sqft);
    }, 0) / prices.length;
    
    return {
      averagePrice,
      averagePricePerSqft,
      priceRange: {
        min: Math.min(...prices),
        max: Math.max(...prices)
      }
    };
  };

  const generateFallbackFeatureResults = () => {
    return {
      lotSize: {
        feature: 'Lot Size',
        unit: '$/sq ft',
        methods: [
          { name: 'Paired Sales Analysis', value: 25, confidence: 75, reasoning: 'Market comparison method' },
          { name: 'Linear Regression', value: 28, confidence: 82, reasoning: 'Statistical correlation R²: 0.72' },
          { name: 'Price/SqFt Allocation', value: 26, confidence: 78, reasoning: '30% allocation method' },
          { name: 'Market Extraction', value: 24, confidence: 74, reasoning: 'Extracted from market data' },
          { name: 'Percentage Method', value: 27, confidence: 76, reasoning: '30% of total property value' }
        ]
      },
      gla: {
        feature: 'GLA',
        unit: '$/sq ft',
        methods: [
          { name: 'Paired Sales Analysis', value: 65, confidence: 88, reasoning: 'Direct property comparisons' },
          { name: 'Linear Regression', value: 68, confidence: 91, reasoning: 'Statistical correlation R²: 0.84' },
          { name: 'Price/SqFt Method', value: 62, confidence: 85, reasoning: 'Market average with 40% adjustment' },
          { name: 'Sales Comparison', value: 66, confidence: 87, reasoning: 'Comparative analysis method' },
          { name: 'Market Extraction', value: 70, confidence: 89, reasoning: 'Statistical extraction method' }
        ]
      },
      fullBaths: {
        feature: 'Full Bath',
        unit: '$',
        methods: [
          { name: 'Paired Sales Analysis', value: 18500, confidence: 85, reasoning: 'Property pair comparisons' },
          { name: 'Grouped Data Analysis', value: 17200, confidence: 82, reasoning: 'Group comparison method' },
          { name: 'Percentage Method', value: 20600, confidence: 78, reasoning: '5.5% of property value' },
          { name: 'Cost Approach', value: 20000, confidence: 75, reasoning: 'Construction cost method' },
          { name: 'Market Extraction', value: 19800, confidence: 83, reasoning: 'Market-extracted value' }
        ]
      },
      halfBaths: {
        feature: 'Half Bath',
        unit: '$',
        methods: [
          { name: 'Extraction Method', value: 6400, confidence: 78, reasoning: '50% of full bathroom value' },
          { name: 'Paired Sales Analysis', value: 6500, confidence: 72, reasoning: 'Market comparison method' },
          { name: 'Percentage Method', value: 6200, confidence: 75, reasoning: '1.5% of property value' }
        ]
      },
      garageSpaces: {
        feature: 'Garage Space',
        unit: '$/space',
        methods: [
          { name: 'Cost Approach Method', value: 8750, confidence: 82, reasoning: '17.5% property value allocation' },
          { name: 'Paired Sales Analysis', value: 8500, confidence: 78, reasoning: 'Market evidence per space' },
          { name: 'Market Rule Method', value: 8500, confidence: 75, reasoning: 'Market-based per space value' }
        ]
      },
      poolFeatures: {
        feature: 'Pool Features', 
        unit: '$',
        methods: [
          { name: 'Market Survey Method', value: 14500, confidence: 75, reasoning: '3.5% of property value' },
          { name: 'Paired Sales Analysis', value: 15000, confidence: 72, reasoning: 'Properties with/without pools' },
          { name: 'Cost Approach Method', value: 15000, confidence: 68, reasoning: 'Depreciated cost approach' }
        ]
      },
      exteriorFeatures: {
        feature: 'Exterior Features',
        unit: '$', 
        methods: [
          { name: 'Hybrid/Blended Method', value: 8200, confidence: 70, reasoning: 'Combined cost and market approach' },
          { name: 'Cost Approach Method', value: 8500, confidence: 68, reasoning: 'Material and labor costs' },
          { name: 'Market Survey Method', value: 7200, confidence: 65, reasoning: 'Agent and appraiser feedback' }
        ]
      },
      conditionQuality: {
        feature: 'Condition/Quality',
        unit: '$/grade',
        methods: [
          { name: 'Controlled Paired Analysis', value: 18500, confidence: 80, reasoning: 'Statistical paired analysis' },
          { name: 'Percentage Adjustment Method', value: 17200, confidence: 75, reasoning: '4.5% adjustment per grade' },
          { name: 'Market Extraction Method', value: 19800, confidence: 78, reasoning: 'Market-derived adjustments' }
        ]
      }
    };
  };

  // Generate scatter plot data for each feature
  const generateScatterData = (featureData, featureConfig) => {
    if (!featureData || !featureData.methods) return { datasets: [] };

    const methods = featureData.methods;
    const colors = [
      '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
      '#06b6d4', '#84cc16', '#f97316', '#ec4899'
    ];

    return {
      datasets: methods.map((method, index) => {
        // FIX: X-axis now shows per sqft for all, hover shows total
        let xAxisValue = method.value; // Per sqft value for X-axis
        let tooltipValue = method.value;
        let totalValue = method.value;

        if (featureConfig.id === 'lotSize' && method.value > 0) {
          const avgLotSize = 1000; // Default lot size for calculation
          totalValue = Math.round(method.value * avgLotSize); // Total price for tooltip
          xAxisValue = method.value; // Per sqft for X-axis
          tooltipValue = `$${totalValue.toLocaleString()} total ($${method.value.toFixed(2)}/sq ft)`;
        } else if (featureConfig.id === 'gla' && method.value > 0) {
          const avgGLA = 2000; // Default GLA size for calculation
          totalValue = Math.round(method.value * avgGLA); // Total GLA value for tooltip
          xAxisValue = method.value; // Per sqft for X-axis
          tooltipValue = `$${totalValue.toLocaleString()} total ($${method.value.toFixed(2)}/sq ft)`;
        } else if (featureConfig.unit.includes('$')) {
          tooltipValue = `$${method.value.toLocaleString()}`;
        } else {
          tooltipValue = `${method.value.toLocaleString()} ${featureConfig.unit}`;
        }

        return {
          label: method.name,
          data: [{
            x: xAxisValue, // Now showing per sqft on X-axis
            y: method.confidence,
            method: method.name,
            reasoning: method.reasoning,
            originalValue: method.value,
            totalValue: totalValue,
            tooltipValue: tooltipValue
          }],
          backgroundColor: colors[index % colors.length],
          borderColor: colors[index % colors.length],
          pointRadius: 8,
          pointHoverRadius: 10,
          showLine: false
        };
      })
    };
  };

  const chartOptions = (feature) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: `${feature.title} - Multiple Methods`,
        font: { size: 14, weight: 'bold' },
        color: '#f3f4f6'
      },
      legend: {
        display: true,
        position: 'bottom',
        labels: { fontSize: 10, color: '#d1d5db' }
      },
      tooltip: {
        backgroundColor: 'rgba(31, 41, 55, 0.9)',
        titleColor: '#f3f4f6',
        bodyColor: '#d1d5db',
        borderColor: '#6b7280',
        borderWidth: 1,
        callbacks: {
          title: (context) => {
            const point = context[0];
            return point.dataset.label;
          },
          label: (context) => {
            const point = context.raw;
            if (feature.id === 'lotSize' || feature.id === 'gla') {
              return [
                `Total Value: $${point.totalValue.toLocaleString()}`,
                `Per Sq Ft: $${point.x.toFixed(2)}`,
                `Confidence: ${point.y}%`,
                `Method: ${point.reasoning}`
              ];
            } else {
              return [
                point.tooltipValue,
                `Confidence: ${point.y}%`,
                `Method: ${point.reasoning}`
              ];
            }
          }
        }
      }
    },
    scales: {
      x: {
        type: 'linear',
        position: 'bottom',
        title: {
          display: true,
          text: feature.id === 'lotSize' ? 'Price per Sq Ft ($)' :
                feature.id === 'gla' ? 'Price per Sq Ft ($)' :
                `${feature.title.replace(' Analysis', '')} Value (${feature.unit})`,
          color: '#d1d5db'
        },
        grid: { display: true, color: 'rgba(75, 85, 99, 0.3)' },
        ticks: { color: '#9ca3af' }
      },
      y: {
        title: {
          display: true,
          text: 'Confidence Level (%)',
          color: '#d1d5db'
        },
        min: 50,
        max: 100,
        grid: { display: true, color: 'rgba(75, 85, 99, 0.3)' },
        ticks: { color: '#9ca3af' }
      }
    },
    interaction: {
      intersect: false,
      mode: 'point'
    }
  });

  // Render feature card
  const renderFeatureCard = (featureConfig) => {
    const featureData = featureResults?.[featureConfig.id];

    if (!featureData) {
      return (
        <div key={featureConfig.id} className="bg-gray-800 rounded-lg shadow-md p-4 border border-gray-700">
          <h3 className="text-lg font-semibold mb-2 text-gray-100">{featureConfig.title}</h3>
          <div className="flex items-center justify-center h-64 text-gray-400">
            <Loader2 className="w-8 h-8 animate-spin mr-2" />
            Loading data...
          </div>
        </div>
      );
    }

    const chartData = generateScatterData(featureData, featureConfig);
    const avgValue = featureData.methods.reduce((sum, method) => sum + method.value, 0) / featureData.methods.length;
    const avgConfidence = featureData.methods.reduce((sum, method) => sum + method.confidence, 0) / featureData.methods.length;

    // Calculate editable value (5-10% less than average) - initialize if not set
    const editableKey = featureConfig.id;
    if (!editableValues[editableKey]) {
      const reduction = 0.05 + (Math.random() * 0.05); // 5-10% reduction
      const reducedValue = avgValue * (1 - reduction);
      setEditableValues(prev => ({ ...prev, [editableKey]: reducedValue.toFixed(2) }));
    }

    // Display values
    let displayAvgValue = avgValue;
    let displayUnit = featureConfig.unit;

    return (
      <div key={featureConfig.id} className="bg-gray-800 rounded-lg shadow-md p-4 border border-gray-700 hover:border-gray-600 transition-colors">
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-gray-100">{featureConfig.title}</h3>
              <div className="flex items-center gap-2 bg-gray-700 px-3 py-1 rounded">
                <span className="text-xs text-gray-400">Value per sqft:</span>
                <input
                  type="number"
                  step="0.01"
                  value={editableValues[editableKey] || avgValue.toFixed(2)}
                  onChange={(e) => setEditableValues(prev => ({ ...prev, [editableKey]: e.target.value }))}
                  className="w-20 bg-gray-600 text-white text-sm px-2 py-1 rounded border border-gray-500 focus:border-blue-500 focus:outline-none"
                />
                <span className="text-xs text-gray-400">$</span>
              </div>
            </div>
            <p className="text-sm text-gray-400 mt-1">{featureData.methods.length} calculation methods</p>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold text-blue-400">
              ${avgValue.toFixed(2)}/sq ft
            </div>
            <div className="text-sm text-gray-500">
              {Math.round(avgConfidence)}% avg confidence
            </div>
          </div>
        </div>

        <div style={{ height: '300px' }} className="bg-gray-900 rounded-lg p-2">
          <Scatter data={chartData} options={chartOptions(featureConfig)} />
        </div>

        <div className="mt-4 border-t border-gray-700 pt-4">
          <h4 className="font-medium text-gray-300 mb-2">Method Details:</h4>
          <div className="space-y-1 max-h-24 overflow-y-auto">
            {featureData.methods.map((method, index) => (
              <div key={index} className="text-xs text-gray-400 flex justify-between">
                <span className="font-medium text-gray-300">{method.name}:</span>
                <span>
                  ${method.value.toFixed(2)}/sq ft ({method.confidence}%)
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (isLoadingFeatures) {
    return (
      <div className="bg-gray-900 min-h-screen p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-blue-400" />
            <h2 className="text-2xl font-bold text-gray-100 mb-2">Analyzing Property Features</h2>
            <p className="text-gray-400">
              Calculating adjustment values using multiple real estate appraisal methods...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-900 min-h-screen p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-400" />
            <h2 className="text-2xl font-bold text-gray-100 mb-2">Analysis Error</h2>
            <p className="text-gray-400 mb-4">{error}</p>
            <button
              onClick={loadFeatureBasedAdjustments}
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Retry Analysis
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-100 flex items-center">
              <Brain className="w-8 h-8 mr-3 text-blue-400" />
              Smart Adjustments - Feature Analysis
            </h1>
            <p className="text-gray-400 mt-2">
              Advanced property feature valuation using multiple appraisal methods
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={loadFeatureBasedAdjustments}
              disabled={isLoadingFeatures}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Refresh Analysis
            </button>
          </div>
        </div>

        {/* Feature Analysis Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
          {FEATURE_CONFIGS.map(featureConfig => renderFeatureCard(featureConfig))}
        </div>

        {/* Footer Info */}
        <div className="mt-8 bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="font-semibold text-gray-100 mb-2 flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            How This Analysis Works
          </h3>
          <div className="text-gray-300 text-sm space-y-2">
            <p>
              • Each graph represents one property feature and shows multiple calculation methods
            </p>
            <p>
              • Each dot represents the result from a different appraisal formula (Paired Sales, Regression, etc.)
            </p>
            <p>
              • X-axis shows the calculated value for that feature, Y-axis shows confidence level
            </p>
            <p>
              • X-axis shows price per square foot, hover to see total value
            </p>
            <p>
              • Values are derived from your subject property and all comparable properties
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartAdjustments;